import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u3_x2_motorisch';

const M1_U3_X2_Motorisch: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const canEditTexts = canEditTextsM1;
  const canUploadImages = canUploadImagesM1;

  // Overrides laden
  const { override, refresh } = useUnitOverride(PAGE_ID);

  // Basistexte
  const base = {
    intro:
      'Motorische Brandschutzklappen (MBSK) sind wesentliche Sicherheitseinrichtungen in Lüftungsanlagen. Sie verhindern im Brandfall die Ausbreitung von Feuer und Rauch in andere Brandabschnitte. Im Gegensatz zu thermisch auslösenden Klappen werden MBSK über einen Motor angesteuert. Dies ermöglicht eine gezielte Ansteuerung und Überwachung der Klappen.',
    funktionsweise:
      'MBSK sind mit Elektromotoren ausgestattet, die das Öffnen und Schließen der Klappen steuern. Es gibt verschiedene Spannungsarten:\n\n• 24V AC/DC: Gängig für kleinere bis mittlere Klappen, geringerer Energieverbrauch.\n• 230V AC: Für größere Klappen oder wenn eine höhere Kraft benötigt wird.\n\nIm Normalbetrieb sind die Klappen oft offen und werden durch eine Federkraft bei Stromausfall oder bei einem Feueralarm geschlossen. Es gibt auch Motoren, die die Klappen offen halten. Die Entscheidung hierzu hängt immer vom Brandschutzkonzept ab.',
    hersteller:
      'Bekannte Hersteller von MBSK sind unter anderem:\n\n• Belimo: Bietet eine breite Palette an Antrieben und Klappen.\n• Schako: Spezialist für Brandschutz- und Entrauchungsklappen.\n• Troax: Hersteller von Brandschutzlösungen für verschiedene Anwendungsbereiche.\n• Caverion\n• Krantz\n\nDiese Hersteller bieten unterschiedliche Modelle und Ausführungen für verschiedene Lüftungssysteme und Gebäudearten.',
    glt:
      "Die Einbindung der MBSK in die Gebäudeleittechnik (GLT) ist entscheidend für eine zuverlässige Überwachung und Steuerung. Die MBSK kommunizieren mit der GLT über:\n\n• Digitaleingaben (DI): Für Rückmeldungen wie 'Klappe AUF' oder 'Klappe ZU' sowie 'Störung'.\n• Digitalausgänge (DO): Für Befehle wie 'Klappe ÖFFNEN' oder 'Klappe SCHLIESSEN'.\n\nTypische Meldungen/Digitaleingaben in die GLT sind:\n\n• Klappe offen: Meldet, dass die Klappe geöffnet ist.\n• Klappe geschlossen: Meldet, dass die Klappe geschlossen ist.\n• Störung: Zeigt eine Fehlfunktion der Klappe oder des Antriebs an.\n• Feueralarm: Auslösung zum Schliessen der Klappe.\n\nDie GLT kann auf diese Meldungen reagieren und zum Beispiel im Brandfall alle MBSK schließen oder einen Alarm auslösen. Es muss aber in jedem Fall noch eine lokale Auslösung der MBSK geben, wenn die GLT ausfällt."
  };

  const blocks = React.useMemo(() => ([
    { id: 'block_intro', heading: undefined as any, body: override?.texts?.['block_intro'] ?? base.intro },
    { id: 'block_funktionsweise', heading: 'Funktionsweise und Spannungsarten', body: override?.texts?.['block_funktionsweise'] ?? base.funktionsweise },
    { id: 'block_hersteller', heading: 'Bekannte Hersteller', body: override?.texts?.['block_hersteller'] ?? base.hersteller },
    { id: 'block_glt', heading: 'Einbindung in die Regelung und GLT', body: override?.texts?.['block_glt'] ?? base.glt }
  ]), [override, base.funktionsweise, base.glt, base.hersteller, base.intro]);

  // TTS aus Blöcken
  const fullPlainText = React.useMemo(() => blocks.map(b => (b.heading ? `${b.heading}. ${b.body}` : b.body)).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  // Admin: Bild ersetzen
  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try { await uploadUnitAsset(PAGE_ID, key, file, token); alert('Bild aktualisiert.'); refresh(); }
    catch (e: any) { alert('Upload fehlgeschlagen: ' + (e?.message || e)); }
  }, [token, refresh, canUploadImages]);

  // Admin: Text speichern
  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try { await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token); alert('Text gespeichert.'); refresh(); }
    catch (e: any) { alert('Speichern fehlgeschlagen: ' + (e?.message || e)); }
  }, [token, refresh, canEditTexts]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'TTS', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  // Bildquellen mit Override-Fallback (unified pattern)
  const imgSrcFunc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const imgSrc = {
    img_0: imgSrcFunc('img_0', '/assets/drawable/bsk_motorisch01.jpeg'),
    img_1: imgSrcFunc('img_1', '/assets/drawable/bsk_anschlussplan.png'),
    img_2: imgSrcFunc('img_2', '/assets/drawable/bsk_anbindung.png')
  } as const;

  return (
    <UnitPageShell title="1.3.2 Motorische Brandschutzklappen" actions={ttsBtn}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Linke Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_intro"
            body={blocks[0].body}
            onSave={(b) => handleTextSave('block_intro', b)}
          />

          <ZoomableImage
            src={imgSrc.img_0}
            alt="Motorische Brandschutzklappe"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_0', f) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_funktionsweise"
            heading="Funktionsweise und Spannungsarten"
            body={blocks[1].body}
            onSave={(b) => handleTextSave('block_funktionsweise', b)}
          />
        </Box>

        {/* Rechte Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ZoomableImage
            src={imgSrc.img_1}
            alt="Anschlussplan BSK"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_1', f) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_hersteller"
            heading="Bekannte Hersteller"
            body={blocks[2].body}
            onSave={(b) => handleTextSave('block_hersteller', b)}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_glt"
            heading="Einbindung in die Regelung und GLT"
            body={blocks[3].body}
            onSave={(b) => handleTextSave('block_glt', b)}
          />

          <ZoomableImage
            src={imgSrc.img_2}
            alt="BSK Anbindung"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_2', f) : undefined}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/units/m1_u3_x99_quiz')}>Zum Quiz</Button>
          </Box>
        </Box>
      </Box>
    </UnitPageShell>
  );
};

export default M1_U3_X2_Motorisch;

